import { useEffect, useState } from "react";
import { UseQueryResult } from "react-query";
import { Contact, contactsService } from "@/app/providers/services";

import { formatUserFullName } from "@/common";
import Icon from "@imminently/decisively-ui/Icon";
import { Typography } from "@mui/material";
import Tabs from "@imminently/decisively-ui/Tabs";
import Button from "@imminently/decisively-ui/Button";
import { SessionTable } from "@/pages/sessions/components/SessionTable";
import { SessionModal } from "@/pages/sessions/SessionModal";
import { getValueAtDate } from "@imminently/decisively-core";

import { ContactCodeView } from "./ContactCodeView";
import { type ContactIdentity, ContactIdentityStatus } from "./ContactIdentityStatus";
import { ContactOrgModelsTab } from "./ContactOrgModelsTab";

const getContactName = (contact: Contact) => {
  // these may not exist
  const firstName = getValueAtDate(contact.firstName, new Date()) as string;
  const lastName = getValueAtDate(contact.lastName, new Date()) as string;
  return formatUserFullName({ firstName, lastName }).trim();
};

const ContactHeader = ({
  contact,
  onClose,
  onIdentityChecked,
}: {
  contact: Contact;
  onClose: () => void;
  onIdentityChecked?: (contactId: string, identity: ContactIdentity) => void;
}) => {
  // Fetching the contact checks its identity provider user, so only the fetched status is shown here.
  const { data: fetched } = contactsService.useGetOne(contact.id) as UseQueryResult<Contact | undefined>;
  const identityId = fetched?.identityId;
  const identitySource = fetched?.identitySource;
  useEffect(() => {
    if (identityId !== undefined) onIdentityChecked?.(String(contact.id), { identityId, identitySource });
  }, [contact.id, identityId, identitySource, onIdentityChecked]);
  const name = getContactName(contact);
  const hasName = name.length > 0;
  return (
    <div className="flex items-center gap-2 p-4 h-[calc(4rem+1px)] border-b">
      <Typography variant="h5">{hasName ? name : contact.id}</Typography>
      {
        hasName ? (
          <Typography variant="caption" color="textSecondary">{contact.id}</Typography>
        ) : null
      }
      <div className="ml-auto flex items-center gap-2">
        {fetched ? <ContactIdentityStatus contact={fetched} /> : null}
        <Button icon onClick={onClose} label="Close"><Icon name="close" /></Button>
      </div>
    </div>
  );
};

export const ContactPanel = ({
  contact,
  onClose,
  onIdentityChecked,
}: {
  contact: Contact;
  onClose: () => void;
  /** Called with the contact's identity provider user once fetching the contact has checked it. */
  onIdentityChecked?: (contactId: string, identity: ContactIdentity) => void;
}) => {
  const [selectedSession, setSelectedSession] = useState<{ id: string; version: string } | null>(null);
  const [value, setValue] = useState<"data" | "sessions" | "orgModels">("data");

  const openSessionModal = (session: any) => {
    setSelectedSession({ id: session.id, version: session.version });
  };

  return (
    <>
      <div key={contact.id} className="flex h-full flex-col">
        <ContactHeader contact={contact} onClose={onClose} onIdentityChecked={onIdentityChecked} />
        <Tabs value={value} onValueChange={(v) => setValue(v as "data" | "sessions" | "orgModels")} >
          <Tabs.List>
            <Tabs.Trigger value="data">Data</Tabs.Trigger>
            <Tabs.Trigger value="sessions">Sessions</Tabs.Trigger>
            <Tabs.Trigger value="orgModels">Org Models</Tabs.Trigger>
          </Tabs.List>
        </Tabs>
        <div className="flex-1 overflow-auto">
          {value === "data" ? <ContactCodeView contact={contact} /> : null}
          {value === "sessions" ? <SessionTable filter={{ contacts: `$member=${contact.id}` }} onSessionClick={openSessionModal} /> : null}
          {value === "orgModels" ? <ContactOrgModelsTab contact={contact} /> : null}
        </div>
      </div>
      {selectedSession ? (
        <SessionModal
          sessionId={selectedSession.id}
          versionId={selectedSession.version}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedSession(null);
            }
          }}
        />
      ) : null}
    </>
  );
};
